import { Box, Button, Typography, IconButton, Dialog, DialogContent, DialogContentText, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { useState } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import TimelineComponent from './TimeLineComponent'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import { GET_ESCALATION_COLOR } from '../../constants/SeverityColors'
import { convertToPixelFromVH, convertToPixelFromVW } from '../../utils/ViewportUtils'

export default function FunctionalLocationInfo({ flRow }: { flRow: any }) {

    const [showDetails, setShowDetails] = useState(false);
    const [showTimeline, setShowTimeline] = useState(false);

    const dialogWidth = 40;
    const dialogHeight = (flRow.timeline === null) ? 25 : 50;

    return (
        <>
            <Dialog
                open={showTimeline}
                onClose={() => setShowTimeline(prev => !prev)}
                PaperProps={{
                    sx: {
                        display: 'grid',
                        placeContent: 'center'
                    }
                }}
                sx={{
                    background: '#CCCCCC87',
                    '& .MuiDialog-paper': {
                        boxShadow: 'none',
                        filter: 'drop-shadow(0px 0px 2px #009CA4) drop-shadow(0px 0px 15px black)',
                        backgroundColor: 'transparent !important'
                    }
                }}>
                <DialogContent sx={{
                    width: `${dialogWidth}vw`,
                    height: `${dialogHeight}vh`,
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--text-light)',
                    clipPath: `path('M 30 0 H ${convertToPixelFromVW(dialogWidth)} V ${convertToPixelFromVH(dialogHeight) - 30} L ${convertToPixelFromVW(dialogWidth)- 30} ${convertToPixelFromVH(dialogHeight)} H 0 V 30 Z')`,
                }}>
                    <DialogContentText component={'div'}>
                        <Box component={'div'} sx={{
                            width: `${dialogWidth - 1}vw`,
                            backgroundColor: 'var(--text-light)',
                            position: 'absolute',
                            paddingTop: 2,
                            zIndex: 2,
                            top: 0,
                            display: 'grid',
                            gridTemplateColumns: '1fr 0.25fr',
                            boxSizing: 'border-box',
                            overflowX: 'hidden',
                            opacity: '1 !important',
                        }}>
                            <Box sx={{
                                borderBottom: '2px solid #000000',
                            }}>
                                <Typography variant='h5'>
                                    Timeline
                                </Typography>
                                <Typography variant='subtitle1'>
                                    {flRow.description_of_functional_location}
                                </Typography>
                            </Box>
                            <Box sx={{
                                marginLeft: 'auto',
                                width: '33.3%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                                <IconButton disableRipple
                                    disableFocusRipple
                                    onClick={() => setShowTimeline(prev => !prev)}
                                    sx={{
                                        transition: 'all 200ms ease-in-out',
                                        '&:hover': {
                                            scale: 1.25,
                                        }
                                    }}>
                                    <CloseIcon htmlColor='#000000' />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={{
                            position: 'relative',
                            top: '4vw',
                        }}>
                            <TimelineComponent list={flRow.timeline} rating={flRow.escalation_rating} viewFull={true}></TimelineComponent>
                        </Box>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
            <Accordion elevation={0}
                onChange={() => setShowDetails(prev => !prev)}
                sx={{
                    border: 'none',
                    borderRadius: 0,
                }}>
                <AccordionSummary
                    LinkComponent={Box}
                    sx={{
                        padding: 0,
                        display: 'grid',
                        gridTemplateColumns: '0.1fr 1fr 1.5fr 1fr 1fr 1fr 1fr',
                        columnGap: 1,
                        transition: 'backround 200ms ease-in-out',
                        '& .MuiAccordionSummary-content': {
                            display: 'contents !important'
                        },
                        '& .MuiAccordion-heading': {
                            padding: 0,
                            lineHeight: 1,
                        }
                    }}>
                    <Box>
                        <KeyboardArrowRightIcon sx={{
                            transition: 'all 200ms linear',
                            transform: `rotate(${!showDetails ? 0 : 90}deg)`
                        }} />
                    </Box>
                    <Box>
                        <Typography fontWeight={700}>{flRow.functional_location}</Typography>
                    </Box>
                    <Box>
                        <Typography fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>{flRow.description_of_functional_location}</Typography>
                    </Box>
                    <Box>{flRow.hseces_category}</Box>
                    <Box>{flRow.hseces_description}</Box>
                    <Box>
                        <Typography
                            color={GET_ESCALATION_COLOR(flRow.escalation_rating)}
                            sx={{
                                fontWeight: 700,
                                textTransform: 'capitalize'
                            }}>
                            {flRow.escalation_rating}
                        </Typography>
                    </Box>
                    <Box>
                        {
                            flRow.escalation_rating !== 'Low' && <Button variant='text'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setShowTimeline(prev => !prev);
                                }}
                                sx={{
                                    fontWeight: 600,
                                    textDecoration: 'underline',
                                    color: '#00A9B1',
                                    padding: 0,
                                }}>
                                View Full Timeline
                            </Button>
                        }
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Typography variant='subtitle1'>
                            Summary of Location Health
                        </Typography>
                        {flRow.conclusion}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Typography variant='subtitle1'>
                            Timeline
                        </Typography>
                        <TimelineComponent list={flRow.timeline} rating={flRow.escalation_rating}></TimelineComponent>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        '& > *': { margin: 0, display: 'inline-block', maxWidth: '100%', },
                        '& > pre': { background: '#FFFFFF0D', padding: 1, borderLeft: '2px solid grey' },
                        '& > table': {
                            borderCollapse: 'collapse',
                            maxWidth: '100%',
                            overflowY: 'hidden',
                            overflowX: 'scroll',
                            '& th, & td': {
                                padding: 1,
                                border: '1px solid #d0dada',
                            },
                            '& thead tr, & tr:nth-of-type(even)': {
                                background: '#daf3f3'
                            }
                        }
                    }}>
                        <Typography variant='subtitle1'>
                            Data View
                        </Typography>
                        <Markdown remarkPlugins={[remarkGfm]}>
                            {flRow.data_view}
                        </Markdown>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </>
    )
}
